import React, { useMemo, useEffect, useState } from 'react'
import { AppTable, FlexRowBetween, AppRemoveButtonPopup, FlexBox, FlexRowWrap } from 'components'
import { Card, CardContent, Label } from 'semantic-ui-react'
import { DateUtility, CommonUtility } from 'utility'
import { AppPrimaryButton } from 'elements'
import { SupportTicketService } from 'utility/services/support-ticket'

// Status mapping
const STATUS_MAP = {
    1: { text: 'Open', color: 'blue' },
    2: { text: 'In Progress', color: 'orange' },
    3: { text: 'Resolved', color: 'green' },
    4: { text: 'Closed', color: 'grey' },
}

const getStatusLabel = (statusId) => {
    const status = STATUS_MAP[statusId] || STATUS_MAP[1]
    return (
        <Label color={status.color} size="small">
            {status.text}
        </Label>
    )
}

const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
        return DateUtility.dateToLocalISO(new Date(dateString), 'MM/dd/yyyy')
    } catch {
        return dateString
    }
}

const SupportTicketCard = ({ item, onView, onDelete, issueTypes }) => {
    const getIssueTypeName = (issueTypeId) => {
        if (!issueTypeId || !issueTypes.length) return 'N/A'
        const issueType = issueTypes.find(type => type.IssueTypeId === issueTypeId)
        return issueType?.IssueTypeName || 'N/A'
    }

    return (
        <Card fluid>
            <CardContent>
                <FlexRowBetween>
                    <h4 title={item.TicketNumber || `Ticket #${item.TicketId}`}>
                        {CommonUtility.truncateString(item.TicketNumber || `Ticket #${item.TicketId}`, 20)}
                    </h4>
                    <span>
                        {getStatusLabel(item.StatusId)}
                    </span>
                </FlexRowBetween>
                <h5 className="mt-0">
                    {getIssueTypeName(item.IssueTypeId)} • {formatDate(item.CreatedOn)}
                </h5>
                <div style={{ marginBottom: '8px' }}>
                    <strong>Message:</strong> {CommonUtility.truncateString(item.Message || 'N/A', 100)}
                </div>
                {item.PhoneNumber && (
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Phone:</strong> {item.PhoneNumber}
                    </div>
                )}
                {item.Attachments && item.Attachments.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                        <strong>Attachments:</strong> {item.Attachments.length} file(s)
                    </div>
                )}
                <FlexRowWrap className="justify-content-end">
                    <AppPrimaryButton
                        onClick={() => onView(item)}
                        type="button"
                    >
                        View Details
                    </AppPrimaryButton>
                    {onDelete && (
                        <AppRemoveButtonPopup
                            onConfirm={() => onDelete(item.TicketId)}
                        />
                    )}
                </FlexRowWrap>
            </CardContent>
        </Card>
    )
}

export const SupportTicketList = ({ data, onView, onDelete }) => {
    const [issueTypes, setIssueTypes] = useState([])

    useEffect(() => {
        const fetchIssueTypes = async () => {
            try {
                const types = await SupportTicketService.getIssueTypes()
                setIssueTypes(Array.isArray(types) ? types : [])
            } catch (error) {
                console.error('Error fetching issue types:', error)
                setIssueTypes([])
            }
        }
        fetchIssueTypes()
    }, [])

    const getIssueTypeName = (issueTypeId) => {
        if (!issueTypeId || !issueTypes.length) return 'N/A'
        const issueType = issueTypes.find(type => type.IssueTypeId === issueTypeId)
        return issueType?.IssueTypeName || 'N/A'
    }

    const columns = useMemo(
        () => [
            {
                title: 'Ticket Number',
                render: (item) => item.TicketNumber || `Ticket #${item.TicketId}`,
            },
            {
                title: 'Issue Type',
                render: (item) => getIssueTypeName(item.IssueTypeId),
            },
            {
                title: 'Status',
                render: (item) => getStatusLabel(item.StatusId),
            },
            {
                title: 'Message',
                render: (item) => (
                    <span>
                        {CommonUtility.truncateString(item.Message || 'N/A', 50)}
                    </span>
                ),
            },
            {
                title: 'Created Date',
                render: (item) => (
                    <span>
                        {formatDate(item.CreatedOn)}
                    </span>
                ),
            },
            {
                title: 'Actions',
                render: (item) => (
                    <FlexBox className="justify-content-center">
                        <AppPrimaryButton
                            onClick={() => onView(item)}
                            type="button"
                        >
                            View
                        </AppPrimaryButton>
                        {onDelete && (
                            <AppRemoveButtonPopup
                                onConfirm={() => onDelete(item.TicketId)}
                            />
                        )}
                    </FlexBox>
                ),
            },
        ],
        [onView, onDelete, issueTypes],
    )

    return (
        <AppTable
            data={data}
            columns={columns}
            emptyMessage="No support tickets found"
            mobileCard={
                <SupportTicketCard
                    onView={onView}
                    onDelete={onDelete}
                    issueTypes={issueTypes}
                />
            }
        />
    )
}
